/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';
import {
  useLocation,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { GlobalContext } from '../api/GlobalContext';
import { useContext, useEffect, useState } from 'react';
// reactstrap components
import { Container } from 'reactstrap';
// core components
import UserNavbar from 'components/Navbars/UserNavbar.js';
import UserFooter from 'components/Footers/UserFooter.js';
import Sidebar from 'components/Sidebar/Sidebar.js';
import FloatingBugButton from 'components/Misc/FloatingBugButton.js';
import OnboardingChecklist from 'components/Onboarding/OnboardingChecklist.js';
import Synapse from 'components/Misc/Synapse/Synapse.js';
import ComprehendLive from 'components/ComprehendLive/ComprehendLive.js';
import VendorAppHost from 'components/VendorApps/VendorAppHost.js';
import VoiceTrainingInvite from 'views/voiceTraining/VoiceTrainingInvite.js';
import vendorStore, { isVendorAppsEnabled } from 'utils/vendorApps/store';
import routes from 'routes.js';
import { detectOSType } from '../api/DeviceInfo';
import IntegrationSwitch from '../components/IntegrationSwitch/IntegrationSwitch';
import { sendNativeMessage, isNativeRecorder } from '../api/nativeRecorder';
const platform = sessionStorage.getItem('platform');
const isMobileOrTablet = ['iOS Device', 'Tablet', 'Mobile'].includes(
  detectOSType(),
);

const User = (props) => {
  const navigate = useNavigate();

  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  // Credentials must reach the native shell even with no visit open, otherwise
  // a Watch tap on the conversations list has no session to act with.
  useEffect(() => {
    // ReactNativeWebView is injected before content loads; sessionStorage
    // 'platform' only lands on load-end, which this effect can beat.
    let isNativeHost = false;
    try {
      isNativeHost = !!window.ReactNativeWebView || isNativeRecorder();
    } catch (_) {}
    if (!isNativeHost) return;
    if (apiClient?.sessionKey) sendNativeMessage('sessionKey', apiClient.sessionKey);
    if (apiClient?.sessionEmail) sendNativeMessage('sessionEmail', apiClient.sessionEmail);
  }, [apiClient, apiClient?.sessionKey, apiClient?.sessionEmail]);

  // Apple Watch "New Visit": the native shell relays the command here so the
  // web app stays the only thing that creates conversations. On success we
  // navigate, which primes the native recorder with the new visit.
  useEffect(() => {
    const createVisitFromWatch = () => {
      apiClient
        .newConversation('')
        .then((data) => {
          if (data?.uid) {
            navigate('/User/conversations/details#' + data.uid);
            sendNativeMessage('newVisitResult', { ok: true });
          } else {
            sendNativeMessage('newVisitResult', {
              ok: false,
              error: data?.error || 'Could not create visit',
            });
          }
        })
        .catch(() => {
          sendNativeMessage('newVisitResult', {
            ok: false,
            error: 'Could not create visit',
          });
        });
    };

    const sendVisitList = () => {
      apiClient
        .updateConversations()
        .then(() => {
          const rows = (apiClient.conversations || [])
            .slice()
            .sort((a, b) => new Date(b.lastUsed || 0) - new Date(a.lastUsed || 0))
            .slice(0, 12)
            .map((c) => ({
              id: c.conversationId,
              patient: c.patientName || '',
              name: c.conversationName || '',
            }))
            .filter((c) => c.id != null);
          sendNativeMessage('visitList', { visits: rows });
        })
        .catch(() => sendNativeMessage('visitList', { visits: [] }));
    };

    const handleInbound = (raw) => {
      try {
        const msg = JSON.parse(raw);
        if (msg?.type === 'newVisit') createVisitFromWatch();
        else if (msg?.type === 'listVisits') sendVisitList();
        else if (msg?.type === 'openVisit' && msg.value?.id != null) {
          navigate('/User/conversations/details#' + msg.value.id);
        }
      } catch (_) {}
    };
    const onDocument = (e) => {
      if (typeof e?.data === 'string') handleInbound(e.data);
    };
    const onWindow = (e) => {
      if (!e?.data) return;
      if (e.data.__comp === true && e.data.type === 'RN_MESSAGE') {
        if (typeof e.data.data === 'string') handleInbound(e.data.data);
        return;
      }
      if (typeof e.data === 'string') handleInbound(e.data);
    };

    document.addEventListener('message', onDocument);
    window.addEventListener('message', onWindow);
    return () => {
      document.removeEventListener('message', onDocument);
      window.removeEventListener('message', onWindow);
    };
  }, [apiClient, navigate]);

  // Track Synapse visibility locally and let ApiClient notify us when profile loads
  const [synapseEnabled, setSynapseEnabled] = useState(
    Boolean(apiClient.features?.synapse),
  );
  // agentic_chat: the footer's text moves into the sidebar (see Sidebar.js)
  const [agenticChatEnabled, setAgenticChatEnabled] = useState(
    Boolean(apiClient.features?.agentic_chat),
  );
  // The Comprehend Live tray serves two feature tiers off one surface:
  // `comprehend_live` = the full ambient experience; `multirecord_tray` =
  // the V0.5 MultiRecord++ tray (vertical timeline + auto-switcher +
  // schedule loader, no ambient brain). Ambient wins if both are on; the
  // tray itself reads which mode to render. See docs/comprehend-live-v05.md.
  const [comprehendLiveEnabled, setComprehendLiveEnabled] = useState(
    Boolean(
      apiClient.features?.comprehend_live ||
        apiClient.features?.multirecord_tray,
    ),
  );

  // "Help train Hey Comprehend" invite. Web app only: the extension side panel
  // is too narrow and its chrome-extension:// origin can't open the page, and
  // the native recorder's web view has no mic for it.
  const voiceInviteAllowed =
    window.location.protocol !== 'chrome-extension:' && !isNativeRecorder();
  const [voiceInviteEnabled, setVoiceInviteEnabled] = useState(
    Boolean(apiClient.features?.wake_word_collection),
  );

  // Vendor Apps iframe host lives here, outside <Routes>, so leaving the Apps
  // tab never unmounts a vendor's page (docs/vendor-apps-contract.md §5).
  // The Dev Console can force it on locally without the backend flag.
  // Never inside the Chrome extension — see isVendorAppsEnabled.
  const [vendorAppsEnabled, setVendorAppsEnabled] = useState(
    isVendorAppsEnabled(apiClient),
  );
  useEffect(
    () =>
      vendorStore.on('devMode', () =>
        setVendorAppsEnabled(isVendorAppsEnabled(apiClient)),
      ),
    [apiClient],
  );

  useEffect(() => {
    const handler = (client) => {
      setSynapseEnabled(Boolean(client.features?.synapse));
      setAgenticChatEnabled(Boolean(client.features?.agentic_chat));
      setComprehendLiveEnabled(
        Boolean(
          client.features?.comprehend_live || client.features?.multirecord_tray,
        ),
      );
      setVendorAppsEnabled(isVendorAppsEnabled(client));
      setVoiceInviteEnabled(Boolean(client.features?.wake_word_collection));
      document.body.classList.toggle(
        'cpt-visits-schedule',
        client.features?.visits_schedule === true,
      );
    };
    if (apiClient.onProfileUpdated) {
      apiClient.onProfileUpdated(handler);
    }
    // In case profile already loaded before this component mounted
    handler(apiClient);
    return () => {
      if (apiClient.offProfileUpdated) {
        apiClient.offProfileUpdated(handler);
      }
    };
  }, [apiClient]);

  // If our session key is null we should redirect to login
  // Otherwise load the homepage
  useEffect(() => {
    apiClient.checkExpiration().then((data) => {
      if (data === false) {
        console.log(
          'Went home and got expired or bad session key. Forwarding to Login',
        );
        navigate('/auth/login');
      }
    });
  }, [apiClient, navigate]);

  const mainContent = React.useRef(null);
  const location = useLocation();

  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainContent.current.scrollTop = 0;
    if (window.Appcues) {
      window.Appcues.page();
    }
  }, [location]);

  const getRoutes = (routes) => {
    // if apiClient.is_beta, make any route with beta: true visible by making show: true
    if (apiClient.is_beta) {
      routes.forEach((route) => {
        if (route.beta) {
          route.show = true;
        }
      });
    }
    return routes.map((prop, key) => {
      if (prop.layout === '/User') {
        return (
          <Route path={prop.path} element={prop.component} key={key} exact />
        );
      } else {
        return null;
      }
    });
  };

  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      if (
        props?.location?.pathname.indexOf(routes[i].layout + routes[i].path) !==
        -1
      ) {
        return routes[i].name;
      }
    }
    return 'Brand';
  };

  return (
    <>
      {platform !== 'ios' && !isMobileOrTablet && <FloatingBugButton />}
      {platform !== 'ios' && !isMobileOrTablet && <OnboardingChecklist />}
      <Sidebar
        {...props}
        routes={routes}
        logo={{
          innerLink: '/User/home',
          imgSrc: require('../assets/img/brand/AppBanner.png'),
          imgAlt: '...',
        }}
      />
      <div className="main-content" ref={mainContent}>
        <IntegrationSwitch />
        <UserNavbar
          {...props}
          brandText={getBrandText(props?.location?.pathname)}
        />
        {comprehendLiveEnabled && <ComprehendLive />}
        {vendorAppsEnabled && <VendorAppHost />}
        {voiceInviteEnabled && voiceInviteAllowed && <VoiceTrainingInvite />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Container fluid>
          {synapseEnabled && <Synapse />}
          {/* agentic_chat: the footer's text lives in the sidebar, so the bottom edge stays free for the chat input */}
          {!agenticChatEnabled && <UserFooter />}
        </Container>
      </div>
    </>
  );
};

export default User;
